// -----------------------------------------------------------------------------
// TrialEngine — ingredienti combinabili + scheduler imprevedibile + memoria a
// FINESTRA. Nessuna progressione: la difficoltà nasce dalla combinazione delle
// prove, non dallo streak. Timer gestito altrove (6s fissi, nessuna chiusura
// anticipata). Ogni prova produce sempre requiredTaps intero 0..ANSWER_MAX.
// -----------------------------------------------------------------------------
import {
  ANSWER_MAX,
  createGameMemory,
  GameMemory,
  NumberThread,
  RenderSegment,
  SeenThread,
  Trial,
} from "./types";

// Compat con Diagnostics (nessuna progressione: valore informativo).
export function levelFromStreak(_streak: number): number {
  void _streak;
  return 1;
}

const ri = (a: number, b: number) => Math.floor(Math.random() * (b - a + 1)) + a;
const pick = <T,>(a: T[]): T => a[Math.floor(Math.random() * a.length)];
// Scelta pesata: entries = [valore, peso]. Usata per ritarare il MIX interno
// (meno aritmetica pura, più attenzione/trabocchetti) senza cambiare le prove.
const pickW = <T,>(entries: readonly (readonly [T, number])[]): T => {
  const total = entries.reduce((s, [, w]) => s + w, 0);
  let r = Math.random() * total;
  for (const [v, w] of entries) { r -= w; if (r <= 0) return v; }
  return entries[entries.length - 1][0];
};
// n numeri DISTINTI casuali in [lo,hi] (per confronti multipli/ordinali).
const distinctNums = (n: number, lo: number, hi: number): number[] => {
  const pool: number[] = [];
  for (let v = lo; v <= hi; v++) pool.push(v);
  for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [pool[i], pool[j]] = [pool[j], pool[i]]; }
  return pool.slice(0, Math.min(n, pool.length));
};

const ANIMALS = [
  { key: "coniglio", emoji: "🐰", legs: 4, plural: "conigli", g: "m" },
  { key: "gatto", emoji: "🐱", legs: 4, plural: "gatti", g: "m" },
  { key: "cane", emoji: "🐶", legs: 4, plural: "cani", g: "m" },
  { key: "criceto", emoji: "🐹", legs: 4, plural: "criceti", g: "m" },
  { key: "topo", emoji: "🐭", legs: 4, plural: "topi", g: "m" },
  { key: "scoiattolo", emoji: "🐿️", legs: 4, plural: "scoiattoli", g: "m" },
  { key: "pulcino", emoji: "🐥", legs: 2, plural: "pulcini", g: "m" },
  { key: "riccio", emoji: "🦔", legs: 4, plural: "ricci", g: "m" },
  { key: "rana", emoji: "🐸", legs: 4, plural: "rane", g: "f" },
  { key: "tartaruga", emoji: "🐢", legs: 4, plural: "tartarughe", g: "f" },
];
const COLORS = [
  { key: "rosso", emoji: "🔴" },
  { key: "blu", emoji: "🔵" },
  { key: "verde", emoji: "🟢" },
  { key: "giallo", emoji: "🟡" },
  { key: "arancione", emoji: "🟠" },
  { key: "bianco", emoji: "⚪" },
];
const SHAPES = [
  { key: "quadrato", emoji: "⬜", plural: "quadrati", g: "m" },
  { key: "triangolo", emoji: "🔺", plural: "triangoli", g: "m" },
  { key: "cerchio", emoji: "⭕", plural: "cerchi", g: "m" },
  { key: "stella", emoji: "⭐", plural: "stelle", g: "f" },
];
const WORDS = [
  "CANE", "SOLE", "GATTO", "PONTE", "LIBRO", "MARE", "LUNA", "PIZZA",
  "FIORE", "TRENO", "NAVE", "STELLA", "RUOTA", "CASA", "PANE", "NOTTE",
  "FUOCO", "VENTO", "PRATO", "SEDIA", "PENNA", "SCUOLA", "GIOCO", "FESTA",
];
// Associazioni rapide: elemento -> proprietà numerica (recupero, non calcolo).
// Tutti i valori sono 0..8. La domanda usa un articolo/nome naturale.
const OBJECTS = [
  { key: "bicicletta", emoji: "🚲", q: "ruote", art: "una", val: 2 },
  { key: "triciclo", emoji: "🛺", q: "ruote", art: "un", val: 3 },
  { key: "automobile", emoji: "🚗", q: "ruote", art: "un'", val: 4 },
  { key: "mano", emoji: "✋", q: "dita", art: "una", val: 5 },
  { key: "ragno", emoji: "🕷️", q: "zampe", art: "un", val: 8 },
  { key: "sgabello", emoji: "🪑", q: "gambe", art: "uno", val: 3 },
  { key: "triangolo", emoji: "🔺", q: "lati", art: "un", val: 3 },
  { key: "quadrato", emoji: "⬜", q: "lati", art: "un", val: 4 },
  { key: "stella", emoji: "⭐", q: "punte", art: "una", val: 5 },
  { key: "settimana", emoji: "📅", q: "giorni", art: "una", val: 7 },
  { key: "esagono", emoji: "⬡", q: "lati", art: "un", val: 6 },
];
// Numeri scritti in lettere (0..8) per varianti "di lettura" (twist percettivo).
const NUM_WORDS = ["ZERO", "UNO", "DUE", "TRE", "QUATTRO", "CINQUE", "SEI", "SETTE", "OTTO"];
const numWord = (n: number) => NUM_WORDS[n] || String(n);
// -----------------------------------------------------------------------------
// SURFACE / TEMPLATE — l'anti-ripetizione considera la "forma" della domanda,
// non solo la famiglia tecnica. Due prove di famiglie diverse ma stessa surface
// non compaiono consecutivamente. Ogni id è mappato a una superficie.
// -----------------------------------------------------------------------------
const SURFACE: Record<string, string> = {
  // simple / reazione
  tapN: "TAP", tapWord: "TAP", dontTap: "TAP", plusMinusOne: "TAP",
  // calcolo puro
  sum: "CALC", sub: "CALC", sumMissing: "CALC", subMissing: "CALC", sum3: "CALC",
  goal: "CALC", half: "CALC", double: "CALC",
  // trasformazione
  transformNow: "TRANSFORM", transformNext: "TRANSFORM", transformPrev: "TRANSFORM",
  transformDouble: "TRANSFORM",
  // conta-con-criterio (superfici DISTINTE per non sembrare la stessa prova)
  countShownNow: "CONTA_SUBITO", parity: "CONTA_PARITA", greater: "CONTA_MAGGIORE",
  less: "CONTA_MINORE", equalCount: "CONTA_UGUALE",
  // ragionamento numerico (tra X e Y): posizionale vs valore
  numQuanti: "NUM_QUANTI", numQuale: "NUM_QUALE",
  // parole
  vowels: "PAROLA_VOCALI", consonants: "PAROLA_CONSONANTI", wordLen: "PAROLA_LUNGHEZZA",
  letterCount: "PAROLA_LETTERA", wordCompare: "PAROLA_CONFRONTO",
  // confronto / ordinale
  compare: "CONFRONTO", compareMulti: "CONFRONTO_MULTI", secondLargest: "CONFRONTO_ORDINE",
  median: "CONFRONTO_MEDIANO",
  // serie / pattern
  series: "SERIE_CONTINUA", seriesDown: "SERIE_DECRESCENTE", seriesAlt: "SERIE_ALTERNATA",
  seriesRepeat: "SERIE_RIPETUTA",
  // osserva
  plantNumber: "OSSERVA_NUMERO", observe_animal: "OSSERVA", observe_color: "OSSERVA",
  observe_shape: "OSSERVA", observe_mixed: "OSSERVA",
  // recall
  recallTotal: "RICORDA_QTA", recallCount: "RICORDA_QTA", recallCountNot: "RICORDA_COMPLEMENTO",
  recallNumber: "RICORDA_NUMERO", recallOrder: "RICORDA_ORDINE", recallFirstLast: "RICORDA_ORDINE",
  positionOf: "RICORDA_POSIZIONE",
  // combo memoria x matematica
  mem_half: "MEM_CALC", mem_double: "MEM_CALC", mem_goal: "MEM_CALC", mem_minus: "MEM_CALC",
  memnum_half: "MEM_CALC",
  // condizioni
  condNow: "SE_ALLORA", cond_mem: "SE_ALLORA", cond_num: "SE_ALLORA",
  // associazione / cifrario
  attributeCount: "ASSOCIA", legs: "ASSOCIA", cipher: "CIFRARIO", cipherApply: "CIFRARIO",
  // task-switch / flessibilità
  switchRule: "REGOLA", flipTarget: "REGOLA",
  // interferenza visiva
  stroopInk: "STROOP", stroopSize: "STROOP",
};
const surfaceOf = (id: string, family: string) => SURFACE[id] || family;
const VOWELS = new Set(["A", "E", "I", "O", "U"]);
// Articolo indeterminativo maschile: "uno" davanti a s+consonante, z, gn, ps, pn, x, y; altrimenti "un".
const needsUno = (w: string) => /^(s[bcdfglmnpqrtvz]|z|gn|ps|pn|x|y)/i.test(w);
const artIndef = (g: string, key: string) => (g === "f" ? "una" : needsUno(key) ? "uno" : "un");
// Prove che richiedono percezione visiva immediata (contare/riconoscere ciò che è
// mostrato ORA): vengono escluse SOLO con Accessibilità ON. Non toccano ACC OFF.
const VISUAL_ONLY = new Set(["countShownNow", "condNow", "stroopInk", "stroopSize"]);

type Content = {
  id: string;
  family: string;
  prompt: string;
  speech: string;
  requiredTaps: number;
  explanation?: string;
  render?: RenderSegment[];
  apply?: (m: GameMemory) => void;
};

const PROMPT_CAP = 140;
const valid = (c: Content) =>
  Number.isInteger(c.requiredTaps) &&
  c.requiredTaps >= 0 &&
  c.requiredTaps <= ANSWER_MAX &&
  c.prompt.length <= PROMPT_CAP;

const label = (kind: SeenThread["kind"], key: string, n: number): string => {
  if (kind === "animal") {
    const a = ANIMALS.find((x) => x.key === key)!;
    return n === 1 ? a.key : a.plural;
  }
  if (kind === "shape") {
    const s = SHAPES.find((x) => x.key === key)!;
    return n === 1 ? s.key : s.plural;
  }
  return key; // colori: nome invariato
};

export class TrialEngine {
  private mem: GameMemory = createGameMemory();
  private accessible = false;
  // PACING: garanzia maxConsecutiveZeroTap = 1. Se il turno precedente era passivo
  // (0 tocchi), il turno corrente DEVE essere interattivo (requiredTaps > 0).
  private forceActive = false;
  // Attiva/disattiva la modalità Accessibilità (letta da localStorage in Game.jsx).
  // Con ON esclude alla sorgente le prove visive; con OFF nulla cambia.
  setAccessible(v: boolean): void {
    this.accessible = v;
  }
  reset(): void {
    this.mem = createGameMemory();
  }
  get memory(): GameMemory {
    return this.mem;
  }

  next(_streak: number): Trial {
    void _streak;
    const mem = this.mem;
    mem.turn += 1;
    // Se il turno precedente era passivo (0 tap), questo turno va forzato attivo.
    this.forceActive = mem.lastZeroTap;

    // 1) Chiusura garantita dei fili di memoria "scaduti" (finestra 4..6, hard cap 10),
    //    ora DIVERSIFICATA in modo controllato e fair: un filo scaduto può chiudersi
    //    con recall, combo o condition (non più sempre recall). La famiglia di chiusura
    //    esclude sempre lastFamily → regola invariata "mai due volte la stessa famiglia
    //    di fila". Il filo si chiude comunque entro il turno: nessun filo perso, cap
    //    invariato, fairness intatta (un solo filo per tipo, non ambiguo).
    const dueThread = mem.threads.find((t) => !t.resolved && mem.turn >= t.dueTurn);
    if (dueThread) return this.finalize(this.closeDueThread(dueThread));
    if (
      mem.numberThread &&
      !mem.numberThread.resolved &&
      mem.turn >= mem.numberThread.dueTurn
    ) {
      return this.finalize(this.closeDueNumber(mem.numberThread));
    }

    // 2) Scheduler a famiglie (imprevedibile, con vincoli).
    //    Rete di sicurezza accessibilità: con ACC ON una prova visiva non può mai
    //    essere emessa, nemmeno se sfuggisse alla selezione alla sorgente.
    //    Anti-repeat di SURFACE: per i primi 12 tentativi vieta la stessa superficie
    //    del turno precedente (anche tra famiglie diverse); poi rilassa il vincolo
    //    per garantire SEMPRE una prova valida (nessuno stallo).
    const lastSurface = mem.recentSurfaces[mem.recentSurfaces.length - 1];
    for (let attempt = 0; attempt < 16; attempt++) {
      const family = this.pickFamily();
      const c = this.generate(family);
      if (!c || !valid(c)) continue;
      if (this.accessible && VISUAL_ONLY.has(c.id)) continue;
      if (this.forceActive && c.requiredTaps === 0) continue;
      if (attempt < 12 && surfaceOf(c.id, c.family) === lastSurface) continue;
      return this.finalize(c);
    }
    // Fallback sempre sicuro.
    const n = ri(1, 4);
    return this.finalize({
      id: "tapN",
      family: "simple",
      prompt: `TOCCA\n${n} ${n === 1 ? "VOLTA" : "VOLTE"}`,
      speech: `Tocca ${n} ${n === 1 ? "volta" : "volte"}.`,
      requiredTaps: n,
    });
  }

  // ---- scheduler ------------------------------------------------------------
  private activeThreads(): number {
    return this.mem.threads.filter((t) => !t.resolved).length + (this.mem.numberThread && !this.mem.numberThread.resolved ? 1 : 0);
  }
  private pickFamily(): string {
    const mem = this.mem;
    const families = ["simple", "reason", "observe", "recall", "combo", "condition", "assoc", "cipher", "switch", "stroop"];
    const w: Record<string, number> = {
      simple: 1.9,
      reason: 1.7,
      observe: 1.7,
      recall: 1.6,
      combo: 1.5,
      condition: 1.2,
      assoc: 1.2,
      cipher: 1.0,
      switch: 1.1,
      stroop: 0.9,
    };
    const active = this.activeThreads();
    const hasReady =
      mem.threads.some((t) => !t.resolved && mem.turn >= t.dueTurn) ||
      (mem.numberThread && !mem.numberThread.resolved && mem.turn >= mem.numberThread.dueTurn);
    // Respiro: forza un "simple" ogni 2-3 turni.
    if (mem.sinceSimple >= 3) return "simple";
    // Mantieni memoria attiva: se nessun filo, spingi observe.
    if (active === 0) {
      w.observe *= 2.5;
      w.recall = w.combo = w.condition = 0; // nulla da richiamare
    }
    if (active >= 2) w.observe = 0; // max 2 fili
    if (!hasReady) w.recall = w.combo = w.condition = 0;
    // PACING: dopo un turno passivo, vieta le famiglie che possono essere 0-tap
    // (observe = solo osserva; condition = cond_mem può essere 0). Restano attive
    // simple (senza dontTap), reason, recall, combo — tutte con requiredTaps > 0.
    if (this.forceActive) {
      w.observe = 0;
      w.condition = 0;
    }
    // Mai la stessa famiglia due volte di fila.
    if (mem.lastFamily) w[mem.lastFamily] = 0;
    const entries = families.map((f) => [f, w[f]] as const).filter(([, x]) => x > 0);
    const total = entries.reduce((s, [, x]) => s + x, 0);
    let r = Math.random() * total;
    for (const [f, x] of entries) {
      r -= x;
      if (r <= 0) return f;
    }
    return "simple";
  }

  private generate(family: string): Content | null {
    switch (family) {
      case "simple":
        return this.genSimple();
      case "reason":
        return this.genReason();
      case "observe":
        return this.genObserve();
      case "recall":
        return this.genRecall();
      case "combo":
        return this.genCombo();
      case "condition":
        return this.genCondition();
      case "assoc":
        return this.genAssoc();
      case "cipher":
        return this.genCipher();
      case "switch":
        return this.genSwitch();
      case "stroop":
        return this.genStroop();
    }
    return null;
  }

  // ---- SIMPLE (1-4, 35-36, dontTap) ----------------------------------------
  private genSimple(): Content {
    // Con Accessibilità ON escludi alla sorgente le prove visive (countShown/condNow).
    const weighted: [string, number][] = [
      ["tapN", 1.0],
      ["tapWord", 0.5],
      ["plusMinusOne", 0.7],
      ["sum", 0.5],
      ["sub", 0.5],
      ["sumMissing", 0.45],
      ["subMissing", 0.45],
      ["sum3", 0.35],
      ["dontTap", 1.2],
      ["countShown", 1.0],
      ["transform", 1.5],
      ["condNow", 1.0],
    ];
    const opts0 = this.accessible
      ? weighted.filter(([o]) => o !== "countShown" && o !== "condNow")
      : weighted;
    // PACING: dopo un passivo, escludi dontTap (unica prova simple a 0 tap).
    const opts = this.forceActive ? opts0.filter(([o]) => o !== "dontTap") : opts0;
    const k = pickW(opts);
    if (k === "tapN") {
      const n = ri(1, 6);
      return { id: "tapN", family: "simple", prompt: `TOCCA\n${n} ${n === 1 ? "VOLTA" : "VOLTE"}`, speech: `Tocca ${n} ${n === 1 ? "volta" : "volte"}.`, requiredTaps: n, explanation: `dovevi toccare ${n}.` };
    }
    if (k === "tapWord") {
      const n = ri(2, 6);
      return { id: "tapWord", family: "simple", prompt: `TOCCA\n${numWord(n)} VOLTE`, speech: `Tocca ${numWord(n).toLowerCase()} volte.`, requiredTaps: n, explanation: `${numWord(n)} vale ${n}.` };
    }
    if (k === "plusMinusOne") {
      const up = Math.random() < 0.5;
      const base = up ? ri(1, 7) : ri(1, 8);
      const ans = up ? base + 1 : base - 1;
      return { id: "plusMinusOne", family: "simple", prompt: `TOCCA UNO IN ${up ? "PIÙ" : "MENO"}\nDI ${base}`, speech: `Tocca uno in ${up ? "più" : "meno"} di ${base}.`, requiredTaps: ans, explanation: `uno in ${up ? "più" : "meno"} di ${base} è ${ans}.` };
    }
    if (k === "sum") {
      let a = ri(1, 4), b = ri(1, 4);
      while (a + b > ANSWER_MAX) { a = ri(1, 4); b = ri(1, 4); }
      return { id: "sum", family: "simple", prompt: `QUANTO FA\n${a} + ${b}?`, speech: `Quanto fa ${a} più ${b}? Tocca il risultato.`, requiredTaps: a + b, explanation: `${a} più ${b} fa ${a + b}.` };
    }
    if (k === "sub") {
      const a = ri(4, 8), b = ri(1, 3);
      return { id: "sub", family: "simple", prompt: `QUANTO FA\n${a} − ${b}?`, speech: `Quanto fa ${a} meno ${b}? Tocca il risultato.`, requiredTaps: a - b, explanation: `${a} meno ${b} fa ${a - b}.` };
    }
    if (k === "sumMissing") {
      let a = ri(1, 4), b = ri(1, 4);
      while (a + b > ANSWER_MAX) { a = ri(1, 4); b = ri(1, 4); }
      return { id: "sumMissing", family: "simple", prompt: `COMPLETA\n? + ${b} = ${a + b}`, speech: `Completa: quale numero più ${b} fa ${a + b}? Tocca quel numero.`, requiredTaps: a, explanation: `${a} più ${b} fa ${a + b}.` };
    }
    if (k === "subMissing") {
      const a = ri(4, 8), b = ri(1, 3);
      return { id: "subMissing", family: "simple", prompt: `COMPLETA\n${a} − ? = ${a - b}`, speech: `Completa: ${a} meno quale numero fa ${a - b}? Tocca quel numero.`, requiredTaps: b, explanation: `${a} meno ${b} fa ${a - b}.` };
    }
    if (k === "sum3") {
      let a = ri(1, 3), b = ri(1, 3), c = ri(1, 3);
      while (a + b + c > ANSWER_MAX) { a = ri(1, 3); b = ri(1, 3); c = ri(1, 3); }
      return { id: "sum3", family: "simple", prompt: `QUANTO FA\n${a} + ${b} + ${c}?`, speech: `Quanto fa ${a} più ${b} più ${c}? Tocca il risultato.`, requiredTaps: a + b + c, explanation: `${a} più ${b} più ${c} fa ${a + b + c}.` };
    }
    if (k === "dontTap") {
      return { id: "dontTap", family: "simple", prompt: "NON\nTOCCARE", speech: "Non toccare.", requiredTaps: 0, explanation: "dovevi non toccare." };
    }
    if (k === "countShown") {
      const s = pick(SHAPES);
      const n = ri(2, 6);
      const Q = s.g === "f" ? "QUANTE" : "QUANTI";
      const Ql = s.g === "f" ? "Quante" : "Quanti";
      const dem = s.g === "f" ? "queste" : "questi";
      const listen = Array(n).fill(s.key).join(", ");
      return { id: "countShownNow", family: "simple", prompt: `${Q} ${s.plural.toUpperCase()}?\n${Array(n).fill(s.emoji).join(" ")}`, speech: `Conta ${dem} ${s.plural}: ${listen}. ${Ql} sono in tutto? Tocca quel numero.`, requiredTaps: n, explanation: `ci sono ${n} ${s.plural}.` };
    }
    if (k === "transform") {
      const mode = pick(["half", "double", "next", "prev"]);
      if (mode === "half") { const x = pick([2, 4, 6, 8]); return { id: "transformNow", family: "simple", prompt: `ECCO ${x}.\nTOCCA LA METÀ`, speech: `Ecco il numero ${x}. Tocca la metà.`, requiredTaps: x / 2, explanation: `la metà di ${x} è ${x / 2}.` }; }
      if (mode === "double") { const x = ri(1, 4); return { id: "transformDouble", family: "simple", prompt: `ECCO ${x}.\nTOCCA IL DOPPIO`, speech: `Ecco il numero ${x}. Tocca il doppio.`, requiredTaps: 2 * x, explanation: `il doppio di ${x} è ${2 * x}.` }; }
      if (mode === "next") { const x = ri(0, 7); return { id: "transformNext", family: "simple", prompt: `ECCO ${x}.\nTOCCA IL SUCCESSIVO`, speech: `Ecco il numero ${x}. Tocca il numero successivo.`, requiredTaps: x + 1, explanation: `dopo ${x} viene ${x + 1}.` }; }
      const x = ri(1, 8); return { id: "transformPrev", family: "simple", prompt: `ECCO ${x}.\nTOCCA IL PRECEDENTE`, speech: `Ecco il numero ${x}. Tocca il numero precedente.`, requiredTaps: x - 1, explanation: `prima di ${x} viene ${x - 1}.` };
    }
    // condNow: condizione oggettiva su forma mostrata ORA.
    const s = pick(SHAPES);
    const isStar = s.key === "stella";
    const art = s.g === "f" ? "una" : "un";
    return { id: "condNow", family: "simple", prompt: `SE È UNA STELLA TOCCA 3,\nALTRIMENTI TOCCA 1\n\n${s.emoji}`, speech: `La forma mostrata è ${art} ${s.key}. Se è una stella tocca 3, altrimenti tocca 1.`, requiredTaps: isStar ? 3 : 1, explanation: isStar ? "era una stella: 3." : `era un ${s.key}, non una stella: 1.` };
  }

  // ---- REASON (7-12, series, half/double, compare) --------------------------
  private genReason(): Content {
    const k = pickW([
      ["goal", 0.5],
      ["halfdouble", 0.5],
      ["series", 1.4],
      ["compare", 1.4],
      ["criterion", 1.6],
      ["numeric", 0.7],
      ["words", 1.2],
    ] as [string, number][]);
    if (k === "goal") {
      const a = ri(2, 7);
      return { id: "goal", family: "reason", prompt: `QUANTO MANCA A ${a}\nPER ARRIVARE A 8?`, speech: `Quanto manca a ${a} per arrivare a 8? Tocca quel numero.`, requiredTaps: 8 - a, explanation: `da ${a} a 8 mancano ${8 - a}.` };
    }
    if (k === "halfdouble") {
      if (Math.random() < 0.5) { const x = pick([2, 4, 6, 8]); return { id: "half", family: "reason", prompt: `TOCCA LA METÀ\nDI ${x}`, speech: `Tocca la metà di ${x}.`, requiredTaps: x / 2, explanation: `la metà di ${x} è ${x / 2}.` }; }
      const x = ri(1, 4); return { id: "double", family: "reason", prompt: `TOCCA IL DOPPIO\nDI ${x}`, speech: `Tocca il doppio di ${x}.`, requiredTaps: 2 * x, explanation: `il doppio di ${x} è ${2 * x}.` };
    }
    if (k === "series") return this.genSeries();
    if (k === "compare") return this.genCompare();
    if (k === "criterion") return this.genCriterion();
    if (k === "numeric") return this.genNumeric();
    return this.genWords();
  }

  // ---- RAGIONAMENTO NUMERICO (tra X e Y) ------------------------------------
  // Due varianti rigorosamente separate:
  //  QUANTI = posizionale: conta gli elementi fisicamente TRA le posizioni di X e Y
  //           nella sequenza (X e Y esclusi). Il conteggio = numero di tap (1..6).
  //  QUALE  = valore: l'unico intero mancante TRA i due estremi mostrati. La
  //           risposta (2..6) non viene mai visualizzata.
  private genNumeric(): Content {
    if (Math.random() < 0.5) {
      // QUANTI (posizionale). X e Y possono comparire in qualsiasi ordine.
      const k = ri(1, 6); // elementi in mezzo = risposta
      const extra = ri(0, Math.min(2, 7 - k)); // elementi di contorno (fuori dall'intervallo)
      const before = ri(0, extra);
      const after = extra - before;
      const n = k + 2 + extra; // n <= 9 (pool 1..9)
      const seq = distinctNums(n, 1, 9);
      const X = seq[before];
      const Y = seq[before + 1 + k];
      const [a, b] = Math.random() < 0.5 ? [X, Y] : [Y, X];
      const line = seq.join("   ");
      return {
        id: "numQuanti",
        family: "reason",
        prompt: `QUANTI NUMERI CI SONO\nTRA ${a} E ${b}?\n${line}`,
        speech: `La sequenza è: ${seq.join(", ")}. Quanti numeri ci sono tra ${a} e ${b}?`,
        requiredTaps: k,
        explanation: `tra ${a} e ${b} nella sequenza ci sono ${k} numeri.`,
      };
    }
    // QUALE (valore): intero mancante tra due estremi consecutivi. Risposta 2..6.
    const m = ri(2, 6);
    const X = m - 1, Y = m + 1;
    return {
      id: "numQuale",
      family: "reason",
      prompt: `QUALE NUMERO C'È\nTRA ${X} E ${Y}?`,
      speech: `Quale numero c'è tra ${X} e ${Y}?`,
      requiredTaps: m,
      explanation: `tra ${X} e ${Y} il numero mancante è ${m}.`,
    };
  }

  // ---- PATTERN / SERIE (crescente, decrescente, ciclo, mancante) ------------
  private genSeries(): Content {
    const mode = pick(["up", "down", "cycle"]);
    if (mode === "up") {
      let s = ri(1, 3), d = ri(1, 2);
      while (s + 3 * d > ANSWER_MAX) { s = ri(1, 3); d = ri(1, 2); }
      return { id: "series", family: "reason", prompt: `CONTINUA LA SERIE\n${s}  ${s + d}  ${s + 2 * d}  …`, speech: `Continua la serie: ${s}, ${s + d}, ${s + 2 * d}. Tocca il successivo.`, requiredTaps: s + 3 * d, explanation: `la serie cresce di ${d}: dopo viene ${s + 3 * d}.` };
    }
    if (mode === "down") {
      let s = ri(5, 8), d = ri(1, 2);
      while (s - 3 * d < 0) { s = ri(5, 8); d = ri(1, 2); }
      return { id: "seriesDown", family: "reason", prompt: `CONTINUA LA SERIE\n${s}  ${s - d}  ${s - 2 * d}  …`, speech: `Continua la serie che scende: ${s}, ${s - d}, ${s - 2 * d}. Tocca il successivo.`, requiredTaps: s - 3 * d, explanation: `la serie cala di ${d}: dopo viene ${s - 3 * d}.` };
    }
    // cycle: sequenza che si ripete
    const a = ri(1, 8); let b = ri(1, 8); while (b === a) b = ri(1, 8);
    return { id: "seriesRepeat", family: "reason", prompt: `CONTINUA LA SEQUENZA\n${a}  ${b}  ${a}  ${b}  …`, speech: `Continua la sequenza che si ripete: ${a}, ${b}, ${a}, ${b}. Tocca il successivo.`, requiredTaps: a, explanation: `la sequenza alterna ${a} e ${b}: dopo ${b} torna ${a}.` };
  }

  // ---- CONFRONTO / ORDINE ---------------------------------------------------
  private genCompare(): Content {
    const mode = pick(["two", "multi", "second", "median"]);
    if (mode === "two") {
      const a = ri(1, 8); let b = ri(1, 8); while (b === a) b = ri(1, 8);
      const big = Math.random() < 0.5;
      return { id: "compare", family: "reason", prompt: `TOCCA IL PIÙ ${big ? "GRANDE" : "PICCOLO"}\n${a}     ${b}`, speech: `Tocca il numero più ${big ? "grande" : "piccolo"} tra ${a} e ${b}.`, requiredTaps: big ? Math.max(a, b) : Math.min(a, b), explanation: `il più ${big ? "grande" : "piccolo"} è ${big ? Math.max(a, b) : Math.min(a, b)}.` };
    }
    const nums = distinctNums(mode === "multi" ? ri(3, 4) : 3, 1, 8);
    if (mode === "multi") {
      const big = Math.random() < 0.5;
      const ans = big ? Math.max(...nums) : Math.min(...nums);
      return { id: "compareMulti", family: "reason", prompt: `TOCCA IL PIÙ ${big ? "GRANDE" : "PICCOLO"}\n${nums.join("   ")}`, speech: `Tocca il numero più ${big ? "grande" : "piccolo"} tra ${nums.join(", ")}.`, requiredTaps: ans, explanation: `il più ${big ? "grande" : "piccolo"} è ${ans}.` };
    }
    const sorted = [...nums].sort((x, y) => y - x);
    if (mode === "second") {
      return { id: "secondLargest", family: "reason", prompt: `TOCCA IL SECONDO\nPIÙ GRANDE\n${nums.join("   ")}`, speech: `Tra ${nums.join(", ")}, tocca il secondo numero più grande.`, requiredTaps: sorted[1], explanation: `il secondo più grande è ${sorted[1]}.` };
    }
    // median di 3
    return { id: "median", family: "reason", prompt: `TOCCA QUELLO IN MEZZO\n(PER VALORE)\n${nums.join("   ")}`, speech: `Tra ${nums.join(", ")}, tocca il numero di valore intermedio.`, requiredTaps: sorted[1], explanation: `il valore intermedio è ${sorted[1]}.` };
  }

  // ---- CONTA-CON-CRITERIO (superfici distinte) ------------------------------
  private genCriterion(): Content {
    const mode = pick(["parity", "greater", "less", "equal"]);
    const mk = (fn: (n: number) => boolean) => {
      let nums: number[] = []; let c = 0; let guard = 0;
      do { nums = Array.from({ length: ri(3, 4) }, () => ri(1, 9)); c = nums.filter(fn).length; guard++; } while (c < 1 && guard < 30);
      return { nums, c };
    };
    if (mode === "parity") {
      const even = Math.random() < 0.5; const { nums, c } = mk((n) => (n % 2 === 0) === even);
      return { id: "parity", family: "reason", prompt: `QUANTI ${even ? "PARI" : "DISPARI"}?\n${nums.join("  ")}`, speech: `Quanti numeri ${even ? "pari" : "dispari"} tra ${nums.join(", ")}? Tocca quel numero.`, requiredTaps: c, explanation: `${even ? "pari" : "dispari"}: ${c}.` };
    }
    if (mode === "greater") {
      const x = ri(3, 6); const { nums, c } = mk((n) => n > x);
      return { id: "greater", family: "reason", prompt: `QUANTI MAGGIORI DI ${x}?\n${nums.join("  ")}`, speech: `Quanti numeri maggiori di ${x} tra ${nums.join(", ")}? Tocca quel numero.`, requiredTaps: c, explanation: `maggiori di ${x}: ${c}.` };
    }
    if (mode === "less") {
      const x = ri(4, 7); const { nums, c } = mk((n) => n < x);
      return { id: "less", family: "reason", prompt: `QUANTI MINORI DI ${x}?\n${nums.join("  ")}`, speech: `Quanti numeri minori di ${x} tra ${nums.join(", ")}? Tocca quel numero.`, requiredTaps: c, explanation: `minori di ${x}: ${c}.` };
    }
    const x = ri(2, 7); const { nums, c } = mk((n) => n === x);
    return { id: "equalCount", family: "reason", prompt: `QUANTE VOLTE\nCOMPARE ${x}?\n${nums.join("  ")}`, speech: `Quante volte compare il numero ${x} tra ${nums.join(", ")}? Tocca quel numero.`, requiredTaps: c, explanation: `${x} compare ${c} volte.` };
  }

  // ---- PAROLE ---------------------------------------------------------------
  private genWords(): Content {
    const mode = pick(["vowels", "consonants", "len", "letter", "compare"]);
    const w = pick(WORDS.filter((x) => x.length <= ANSWER_MAX));
    if (mode === "vowels") {
      const c = w.split("").filter((ch) => VOWELS.has(ch)).length;
      return { id: "vowels", family: "reason", prompt: `QUANTE VOCALI\nIN "${w}"?`, speech: `Quante vocali nella parola ${w}? Tocca quel numero.`, requiredTaps: c, explanation: `${w} ha ${c} vocali.` };
    }
    if (mode === "consonants") {
      const c = w.split("").filter((ch) => !VOWELS.has(ch)).length;
      return { id: "consonants", family: "reason", prompt: `QUANTE CONSONANTI\nIN "${w}"?`, speech: `Quante consonanti nella parola ${w}? Tocca quel numero.`, requiredTaps: c, explanation: `${w} ha ${c} consonanti.` };
    }
    if (mode === "len") {
      return { id: "wordLen", family: "reason", prompt: `QUANTE LETTERE\nIN "${w}"?`, speech: `Quante lettere ha la parola ${w}? Tocca quel numero.`, requiredTaps: w.length, explanation: `${w} ha ${w.length} lettere.` };
    }
    if (mode === "letter") {
      const ch = pick(w.split(""));
      const c = w.split("").filter((x) => x === ch).length;
      return { id: "letterCount", family: "reason", prompt: `QUANTE "${ch}"\nIN "${w}"?`, speech: `Quante lettere ${ch} nella parola ${w}? Tocca quel numero.`, requiredTaps: c, explanation: `${w} ha ${c} lettere ${ch}.` };
    }
    // confronto tra due parole: lunghezza della più lunga/corta
    let w2 = pick(WORDS.filter((x) => x.length <= ANSWER_MAX)); let guard = 0;
    while (w2.length === w.length && guard < 20) { w2 = pick(WORDS.filter((x) => x.length <= ANSWER_MAX)); guard++; }
    const longer = Math.random() < 0.5;
    const ans = longer ? Math.max(w.length, w2.length) : Math.min(w.length, w2.length);
    return { id: "wordCompare", family: "reason", prompt: `TOCCA LA LUNGHEZZA\nDELLA PAROLA PIÙ ${longer ? "LUNGA" : "CORTA"}\n${w}   ${w2}`, speech: `Tra ${w} e ${w2}, tocca la lunghezza della parola più ${longer ? "lunga" : "corta"}.`, requiredTaps: ans, explanation: `la parola più ${longer ? "lunga" : "corta"} ha ${ans} lettere.` };
  }

  // ---- OSSERVA (13-17) : semina la finestra --------------------------------
  private genObserve(): Content | null {
    const mem = this.mem;
    // numero da ricordare (thread numerico) oppure sequenza di token.
    if (!mem.numberThread && Math.random() < 0.3) {
      const x = ri(1, 8);
      const nt: NumberThread = { value: x, plantTurn: mem.turn, dueTurn: mem.turn + ri(4, 6), resolved: false };
      return { id: "plantNumber", family: "observe", prompt: `${x}\n\nTOCCA 1 VOLTA`, speech: `${x}. Tocca una volta.`, requiredTaps: 1, explanation: `dovevi toccare una volta e ricordare ${x}.`, apply: (m) => { m.numberThread = nt; } };
    }
    // FAIRNESS: mai due fili vivi dello STESSO tipo (altrimenti una domanda come
    // "quante volte hai visto il bianco?" avrebbe due risposte plausibili). Si
    // sceglie il tipo SOLO tra quelli che non hanno già un filo non risolto.
    const live = mem.threads.filter((t) => !t.resolved);
    const liveKinds = new Set(live.map((t) => t.kind));
    const availableKinds = (["animal", "color", "shape"] as const).filter((k) => !liveKinds.has(k));
    if (availableKinds.length === 0) return null;
    // ROTAZIONE CONTENUTI: preferisci un kind non usato di recente, così due
    // prove di memoria ravvicinate non riusano sempre lo stesso tipo di token.
    const recent = mem.recentKinds.slice(-2);
    const preferred = availableKinds.filter((k) => !recent.includes(k));
    const kind = pick(preferred.length ? preferred : availableKinds);
    let src: { key: string; emoji: string }[] =
      (kind === "animal" ? ANIMALS : kind === "color" ? COLORS : SHAPES) as { key: string; emoji: string }[];
    // HARDENING percettivo: il COLORE bianco (⚪) e il quadrato bianco (⬜) non
    // devono mai coesistere in memoria, così "il colore bianco" non è confondibile
    // con una forma bianca. Si esclude alla sorgente il token in conflitto.
    const liveShapeSquare = live.some((t) => t.kind === "shape" && (t.counts["quadrato"] || 0) > 0);
    const liveColorWhite = live.some((t) => t.kind === "color" && (t.counts["bianco"] || 0) > 0);
    if (kind === "color" && liveShapeSquare) src = src.filter((c) => c.key !== "bianco");
    if (kind === "shape" && liveColorWhite) src = src.filter((s) => s.key !== "quadrato");
    const n = ri(1, 3);
    const order: { key: string; name: string }[] = [];
    const counts: Record<string, number> = {};
    for (let i = 0; i < n; i++) {
      const t = pick(src);
      order.push({ key: t.key, name: t.key });
      counts[t.key] = (counts[t.key] || 0) + 1;
    }
    const emojis = order.map((o) => (src as { key: string; emoji: string }[]).find((s) => s.key === o.key)!.emoji).join(" ");
    const thread: SeenThread = { kind, order, counts, total: n, plantTurn: mem.turn, dueTurn: mem.turn + ri(4, 6), resolved: false };
    return { id: `observe_${kind}`, family: "observe", prompt: `${emojis}`, speech: `${order.map((o) => o.name).join(", ")}.`, requiredTaps: 0, explanation: "dovevi solo osservare e ricordare.", apply: (m) => { m.threads.push(thread); m.recentKinds.push(kind); if (m.recentKinds.length > 3) m.recentKinds.shift(); } };
  }

  // ---- RECALL semplice (18-22) ---------------------------------------------
  private genRecall(): Content | null {
    const mem = this.mem;
    const t = mem.threads.find((x) => !x.resolved && mem.turn >= x.dueTurn);
    if (t) return this.recallFromThread(t);
    if (mem.numberThread && !mem.numberThread.resolved && mem.turn >= mem.numberThread.dueTurn) return this.recallNumber(mem.numberThread);
    return null;
  }
  private recallFromThread(t: SeenThread): Content {
    const noun = t.kind === "animal" ? "animali" : t.kind === "color" ? "colori" : "forme";
    const Q = t.kind === "shape" ? "QUANTE" : "QUANTI";
    const Ql = t.kind === "shape" ? "Quante" : "Quanti";
    const keys = Object.keys(t.counts);
    const onceKeys = keys.filter((k) => t.counts[k] === 1);
    const hasComplement = keys.some((k) => t.total - t.counts[k] >= 1);
    // Modalità eleggibili: la memoria non è più solo "quanti erano".
    const modes: [string, number][] = [["total", 1.0]];
    if (keys.length > 1) modes.push(["count", 1.0]);
    if (keys.length > 1 && hasComplement) modes.push(["complement", 0.9]);
    if (onceKeys.length > 0) modes.push(["position", 1.0]);
    if (t.kind === "animal") modes.push(["order", 1.1]);
    const mode = pickW(modes);

    if (mode === "total") {
      // Per i COLORI la domanda "QUANTI COLORI HAI VISTO?" si riferisce ai colori
      // DISTINTI (es. 🟡🟡 = 1 colore), non al numero di elementi mostrati.
      const ans = t.kind === "color" ? Object.keys(t.counts).length : t.total;
      const expl = t.kind === "color" ? `poco fa avevi visto ${ans} colori diversi.` : `poco fa avevi visto ${ans} ${noun}.`;
      return { id: "recallTotal", family: "recall", prompt: `POCO FA,\n${Q} ${noun.toUpperCase()}\nHAI VISTO?`, speech: `Poco fa, ${Ql.toLowerCase()} ${noun} hai visto? Tocca quel numero.`, requiredTaps: ans, explanation: expl, apply: () => { t.resolved = true; } };
    }
    if (mode === "complement") {
      const key = pick(keys.filter((k) => t.total - t.counts[k] >= 1));
      const lab = label(t.kind, key, 2);
      const ans = t.total - t.counts[key];
      const q = t.kind === "color"
        ? `POCO FA, QUANTI\nNON ERANO ${key.toUpperCase()}?`
        : `POCO FA,\n${Q} NON ERANO\n${lab.toUpperCase()}?`;
      const sp = t.kind === "color"
        ? `Poco fa, quanti elementi non erano ${key}? Tocca quel numero.`
        : `Poco fa, ${Ql.toLowerCase()} non erano ${lab}? Tocca quel numero.`;
      return { id: "recallCountNot", family: "recall", prompt: q, speech: sp, requiredTaps: ans, explanation: `non erano ${key}: ${ans}.`, apply: () => { t.resolved = true; } };
    }
    if (mode === "position") {
      const key = pick(onceKeys);
      const idx = t.order.findIndex((o) => o.key === key) + 1;
      const lab = label(t.kind, key, 1);
      const dispKey = t.kind === "color" ? `il colore ${key}` : lab;
      return { id: "positionOf", family: "recall", prompt: `POCO FA, IN CHE POSIZIONE\nHAI VISTO ${key.toUpperCase()}?\n(1 = PER PRIMO)`, speech: `Poco fa, in che posizione hai visto ${dispKey}? Uno se per primo. Tocca quel numero.`, requiredTaps: idx, explanation: `${dispKey} era in posizione ${idx}.`, apply: () => { t.resolved = true; } };
    }
    if (mode === "order") {
      // Memoria d'ORDINE: attributo (zampe) dell'elemento in una data posizione.
      const ords = ["PRIMO", "SECONDO", "TERZO"];
      const idx = ri(0, t.total - 1);
      const isLast = idx === t.total - 1 && t.total > 1 && Math.random() < 0.5;
      const pos = isLast ? "ULTIMO" : ords[idx];
      const realIdx = isLast ? t.total - 1 : idx;
      const aKey = t.order[realIdx].key;
      const legs = ANIMALS.find((a) => a.key === aKey)!.legs;
      return { id: "recallOrder", family: "recall", prompt: `POCO FA, IL ${pos} ANIMALE:\nQUANTE ZAMPE AVEVA?`, speech: `Poco fa, l'animale in posizione ${pos.toLowerCase()}: quante zampe aveva? Tocca quel numero.`, requiredTaps: legs, explanation: `il ${pos.toLowerCase()} animale (${aKey}) aveva ${legs} zampe.`, apply: () => { t.resolved = true; } };
    }
    // count (per chiave)
    const key = pick(keys);
    const cnt = t.counts[key];
    const lab = label(t.kind, key, 2);
    const gender =
      t.kind === "animal" ? ANIMALS.find((a) => a.key === key)!.g
      : t.kind === "shape" ? SHAPES.find((s) => s.key === key)!.g
      : "m";
    const Qc = gender === "f" ? "QUANTE" : "QUANTI";
    const Qcl = gender === "f" ? "Quante" : "Quanti";
    const q = t.kind === "color"
      ? `POCO FA, QUANTE VOLTE\nHAI VISTO IL COLORE\n${key.toUpperCase()}?`
      : `POCO FA,\n${Qc} ${lab.toUpperCase()}\nHAI VISTO?`;
    const sp = t.kind === "color"
      ? `Poco fa, quante volte hai visto il colore ${key}? Tocca quel numero.`
      : `Poco fa, ${Qcl.toLowerCase()} ${lab} hai visto? Tocca quel numero.`;
    return { id: "recallCount", family: "recall", prompt: q, speech: sp, requiredTaps: cnt, explanation: `poco fa avevi visto ${cnt} ${t.kind === "color" ? `volte il colore ${key}` : lab}.`, apply: () => { t.resolved = true; } };
  }
  private recallNumber(nt: NumberThread): Content {
    return { id: "recallNumber", family: "recall", prompt: `TOCCA IL NUMERO\nCHE DOVEVI\nRICORDARE`, speech: "Tocca il numero che dovevi ricordare.", requiredTaps: nt.value, explanation: `il numero era ${nt.value}.`, apply: () => { nt.resolved = true; } };
  }

  // ---- COMBO memoria × matematica (23-28, 32-33) ---------------------------
  private genCombo(): Content | null {
    const mem = this.mem;
    const t = mem.threads.find((x) => !x.resolved && mem.turn >= x.dueTurn);
    if (t) return this.comboFromThread(t);
    if (mem.numberThread && !mem.numberThread.resolved && mem.turn >= mem.numberThread.dueTurn) {
      return this.comboFromNumber(mem.numberThread);
    }
    return null;
  }
  private comboFromThread(t: SeenThread): Content {
    const key = pick(Object.keys(t.counts));
    const v = t.counts[key];
    const lab = label(t.kind, key, v);
    const seen = t.kind === "color" ? `${v} volte il colore ${key}` : `${v} ${lab}`;
    const ops: { id: string; ok: boolean; ans: number; txt: string; sp: string; exp: string }[] = [
      { id: "mem_half", ok: v % 2 === 0 && v > 0, ans: v / 2, txt: "TOCCA LA METÀ", sp: "Tocca la metà.", exp: `la metà di ${v} è ${v / 2}.` },
      { id: "mem_double", ok: 2 * v <= ANSWER_MAX, ans: 2 * v, txt: "TOCCA IL DOPPIO", sp: "Tocca il doppio.", exp: `il doppio di ${v} è ${2 * v}.` },
      { id: "mem_goal", ok: true, ans: ANSWER_MAX - v, txt: `QUANTE NE MANCANO PER ARRIVARE A ${ANSWER_MAX}?`, sp: `Quante ne mancano per arrivare a ${ANSWER_MAX}?`, exp: `da ${v} a ${ANSWER_MAX} mancano ${ANSWER_MAX - v}.` },
      { id: "mem_minus", ok: v - 1 >= 0, ans: v - 1, txt: "TOCCANE 1 IN MENO", sp: "Toccane una in meno.", exp: `${v} meno 1 fa ${v - 1}.` },
    ];
    const o = pick(ops.filter((op) => op.ok && !(this.forceActive && op.ans === 0)));
    return { id: o.id, family: "combo", prompt: `POCO FA HAI VISTO ${seen.toUpperCase()}.\n${o.txt}`, speech: `Poco fa hai visto ${seen}. ${o.sp}`, requiredTaps: o.ans, explanation: `poco fa avevi visto ${seen}; ${o.exp}`, apply: () => { t.resolved = true; } };
  }
  // Combo su numero ricordato: solo se pari (metà intera). Se dispari ritorna null:
  // la chiusura ricadrà su condition (cond_num) o recall, MAI su un contenuto di
  // famiglia diversa da "combo" (non violare la regola di sequenza).
  private comboFromNumber(nt: NumberThread): Content | null {
    const v = nt.value;
    if (v % 2 === 0) return { id: "memnum_half", family: "combo", prompt: `IL NUMERO CHE RICORDI:\nTOCCA LA METÀ`, speech: "Tocca la metà del numero che dovevi ricordare.", requiredTaps: v / 2, explanation: `il numero era ${v}, la metà è ${v / 2}.`, apply: () => { nt.resolved = true; } };
    return null;
  }

  // ---- CONDITION memoria × condizione (29-31) ------------------------------
  private genCondition(): Content | null {
    const mem = this.mem;
    const t = mem.threads.find((x) => !x.resolved && mem.turn >= x.dueTurn);
    if (t) return this.conditionFromThread(t);
    if (mem.numberThread && !mem.numberThread.resolved && mem.turn >= mem.numberThread.dueTurn) {
      return this.conditionFromNumber(mem.numberThread);
    }
    return null;
  }
  private conditionFromThread(t: SeenThread): Content {
    const key = pick(Object.keys(t.counts));
    const actual = t.counts[key];
    const trueCase = Math.random() < 0.5;
    const claimed = trueCase ? actual : Math.max(0, actual + pick([-1, 1, 2].filter((d) => actual + d >= 0 && actual + d !== actual)) || actual + 1);
    const claim = trueCase ? actual : (claimed === actual ? actual + 1 : claimed);
    const x = ri(2, 3);
    const lab = label(t.kind, key, claim);
    const seen = t.kind === "color" ? `${claim} volte il colore ${key}` : `${claim} ${lab}`;
    const ans = actual === claim ? x : 0;
    return { id: "cond_mem", family: "condition", prompt: `SE POCO FA HAI VISTO\n${seen.toUpperCase()}\nTOCCA ${x}, ALTRIMENTI\nNON TOCCARE`, speech: `Se poco fa hai visto ${seen}, tocca ${x}, altrimenti non toccare.`, requiredTaps: ans, explanation: actual === claim ? `avevi visto proprio ${seen}: ${x}.` : `avevi visto un numero diverso: non dovevi toccare.`, apply: () => { t.resolved = true; } };
  }
  private conditionFromNumber(nt: NumberThread): Content {
    const v = nt.value;
    const even = v % 2 === 0;
    return { id: "cond_num", family: "condition", prompt: `SE IL NUMERO CHE RICORDI\nÈ PARI TOCCA LA METÀ,\nALTRIMENTI TOCCALO`, speech: "Se il numero che dovevi ricordare è pari tocca la metà, altrimenti toccalo.", requiredTaps: even ? v / 2 : v, explanation: even ? `${v} è pari: metà = ${v / 2}.` : `${v} è dispari: tocchi ${v}.`, apply: () => { nt.resolved = true; } };
  }

  // ---- Chiusura diversificata dei fili scaduti -----------------------------
  // Ordine di famiglie di chiusura campionato con peso (recall/combo/condition),
  // ESCLUDENDO sempre lastFamily → mai due volte la stessa famiglia di fila.
  private closureOrder(): ("recall" | "combo" | "condition")[] {
    const cw: Record<string, number> = { recall: 2.4, combo: 2.0, condition: 1.3 };
    const pool = (["recall", "combo", "condition"] as const)
      .filter((f) => f !== this.mem.lastFamily)
      .filter((f) => !(this.forceActive && f === "condition")) // cond_mem può essere 0-tap
      .map((f) => ({ f, w: cw[f] }));
    const out: ("recall" | "combo" | "condition")[] = [];
    while (pool.length) {
      const total = pool.reduce((s, p) => s + p.w, 0);
      let r = Math.random() * total;
      let i = 0;
      for (; i < pool.length - 1; i++) { r -= pool[i].w; if (r <= 0) break; }
      out.push(pool[i].f);
      pool.splice(i, 1);
    }
    return out;
  }
  private closeDueThread(t: SeenThread): Content {
    for (const f of this.closureOrder()) {
      const c = f === "recall" ? this.recallFromThread(t) : f === "combo" ? this.comboFromThread(t) : this.conditionFromThread(t);
      if (c) return c;
    }
    return this.recallFromThread(t); // garanzia: il filo si chiude comunque
  }
  private closeDueNumber(nt: NumberThread): Content {
    for (const f of this.closureOrder()) {
      const c = f === "recall" ? this.recallNumber(nt) : f === "combo" ? this.comboFromNumber(nt) : this.conditionFromNumber(nt);
      if (c) return c;
    }
    return this.recallNumber(nt); // garanzia: il numero si chiude comunque
  }

  // ---- ASSOCIAZIONE (elemento -> proprietà numerica: recupero, non calcolo) --
  private genAssoc(): Content {
    if (Math.random() < 0.5) {
      const a = pick(ANIMALS);
      const artl = artIndef(a.g, a.key);
      return { id: "legs", family: "assoc", prompt: `QUANTE ZAMPE\nHA ${artl.toUpperCase()} ${a.key.toUpperCase()}?`, speech: `Quante zampe ha ${artl} ${a.key}? Tocca quel numero.`, requiredTaps: a.legs, explanation: `${artl} ${a.key} ha ${a.legs} zampe.` };
    }
    const o = pick(OBJECTS);
    return { id: "attributeCount", family: "assoc", prompt: `QUANTE ${o.q.toUpperCase()}\nHA ${o.art.toUpperCase()} ${o.key.toUpperCase()}?`, speech: `Quante ${o.q} ha ${o.art} ${o.key}? Tocca quel numero.`, requiredTaps: o.val, explanation: `${o.art} ${o.key} ha ${o.val} ${o.q}.` };
  }

  // ---- CIFRARIO (mappa mostrata, poi usata: decodifica rapida) ---------------
  private genCipher(): Content {
    const shs = [...SHAPES].sort(() => Math.random() - 0.5);
    const s1 = shs[0], s2 = shs[1];
    const v1 = ri(1, 4); let v2 = ri(1, 4); while (v2 === v1) v2 = ri(1, 4);
    if (Math.random() < 0.5) {
      const target = Math.random() < 0.5 ? { s: s1, v: v1 } : { s: s2, v: v2 };
      return { id: "cipher", family: "cipher", prompt: `${s1.emoji} = ${v1}\n${s2.emoji} = ${v2}\nQUANTE VOLTE PER ${target.s.emoji}?`, speech: `Regola: ${s1.key} vale ${v1}, ${s2.key} vale ${v2}. Quante volte per ${target.s.key}? Tocca quel numero.`, requiredTaps: target.v, explanation: `${target.s.key} vale ${target.v}.` };
    }
    const v = ri(1, 4);
    return { id: "cipherApply", family: "cipher", prompt: `${s1.emoji} = ${v}\nTOCCA IL DOPPIO DI ${s1.emoji}`, speech: `Regola: ${s1.key} vale ${v}. Tocca il doppio di ${s1.key}.`, requiredTaps: v * 2, explanation: `il doppio di ${v} è ${v * 2}.` };
  }

  // ---- TASK-SWITCH / FLESSIBILITÀ (la regola alterna nel tempo) -------------
  private genSwitch(): Content {
    const toggle = this.mem.ruleToggle % 2 === 0;
    const bump = (m: GameMemory) => { m.ruleToggle++; };
    if (Math.random() < 0.5) {
      const even = toggle;
      let nums: number[] = []; let c = 0; let g = 0;
      do { nums = Array.from({ length: ri(3, 4) }, () => ri(1, 9)); c = nums.filter((n) => (n % 2 === 0) === even).length; g++; } while (c < 1 && g < 30);
      return { id: "switchRule", family: "switch", prompt: `CONTA I ${even ? "PARI" : "DISPARI"}\n${nums.join("  ")}`, speech: `Conta i ${even ? "pari" : "dispari"} tra ${nums.join(", ")}. Tocca quel numero.`, requiredTaps: c, explanation: `${even ? "pari" : "dispari"}: ${c}.`, apply: bump };
    }
    const big = toggle;
    const nums = distinctNums(ri(3, 4), 1, 8);
    const ans = big ? Math.max(...nums) : Math.min(...nums);
    return { id: "flipTarget", family: "switch", prompt: `TOCCA IL PIÙ ${big ? "GRANDE" : "PICCOLO"}\n${nums.join("   ")}`, speech: `Tocca il più ${big ? "grande" : "piccolo"} tra ${nums.join(", ")}.`, requiredTaps: ans, explanation: `il più ${big ? "grande" : "piccolo"} è ${ans}.`, apply: bump };
  }

  // ---- INTERFERENZA VISIVA (Stroop) — usa il canale di render strutturato ---
  private genStroop(): Content {
    const HEX: Record<string, string> = { rosso: "#ff4d4d", blu: "#4d7cff", verde: "#33d17a", giallo: "#ffd60a", arancione: "#ff8c00" };
    if (Math.random() < 0.5) {
      const palette = COLORS.filter((c) => c.key !== "bianco");
      const target = pick(palette);
      const n = ri(3, 5);
      const segs: RenderSegment[] = [];
      let count = 0;
      for (let i = 0; i < n; i++) {
        const word = pick(palette);
        const ink = pick(palette);
        if (ink.key === target.key) count++;
        segs.push({ t: word.key.toUpperCase(), c: HEX[ink.key] });
      }
      if (count === 0) { const j = ri(0, n - 1); segs[j] = { t: pick(palette).key.toUpperCase(), c: HEX[target.key] }; count = 1; }
      return { id: "stroopInk", family: "stroop", prompt: `QUANTE SCRITTE IN ${target.key.toUpperCase()}?`, speech: `Conta quante parole sono scritte con l'inchiostro ${target.key}. Tocca quel numero.`, requiredTaps: count, explanation: `scritte in ${target.key}: ${count}.`, render: segs };
    }
    const nums = distinctNums(ri(3, 4), 1, 8);
    const maxVal = Math.max(...nums);
    const bigIdx = nums.indexOf(pick(nums.filter((n) => n !== maxVal)));
    const segs: RenderSegment[] = nums.map((n, i) => ({ t: String(n), s: i === bigIdx ? 2.4 : 1.1 }));
    return { id: "stroopSize", family: "stroop", prompt: `TOCCA LA CIFRA PIÙ GRANDE`, speech: `Tocca la cifra più grande.`, requiredTaps: nums[bigIdx], explanation: `la cifra più grande come dimensione è ${nums[bigIdx]}.`, render: segs };
  }


  // ---- finalize -------------------------------------------------------------
  private finalize(c: Content): Trial {
    const mem = this.mem;
    c.apply?.(mem);
    mem.lastFamily = c.family;
    mem.sinceSimple = c.family === "simple" ? 0 : mem.sinceSimple + 1;
    mem.lastZeroTap = c.requiredTaps === 0;
    // Traccia la superficie emessa (finestra di 3) per l'anti-repeat percepito.
    mem.recentSurfaces.push(surfaceOf(c.id, c.family));
    if (mem.recentSurfaces.length > 3) mem.recentSurfaces.shift();
    // pulizia fili risolti o troppo vecchi (hard cap 10 turni)
    mem.threads = mem.threads.filter((t) => !t.resolved && mem.turn - t.plantTurn < 10);
    if (mem.numberThread && (mem.numberThread.resolved || mem.turn - mem.numberThread.plantTurn >= 10)) mem.numberThread = null;
    return { id: c.id, category: c.family, prompt: c.prompt, speech: c.speech, requiredTaps: c.requiredTaps, explanation: c.explanation, render: c.render };
  }
}
